import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { getPaymentsThematicAreaDistribution } from "common/services/payments.service";
import useQueryState from "Components/Hooks/UseQueryState";
import moment from "moment";
import { FilterType, defaultFilter } from "../Filters";
import useChartColors from "Components/Common/useChartColors";

const convertToSeriesAndLabels = (data: any[]) => {
  // Extract labels from the "name" field
  const labels = data.map((item) => item.name);

  // Extract countSeries from the "totalCount" field
  const countSeries = [
    {
      data: data.map((item) => item.totalCount),
    },
  ];

  // Extract amountSeries from the "totalAmount" field
  const amountSeries = [
    {
      data: data.map((item) => item.totalAmount),
    },
  ];

  return { labels, countSeries, amountSeries };
};

const ContractDistributionChart = ({ series, categories }: any) => {
  const ChartColors = useChartColors("contract_distribution_chart");

  const options: any = {
    chart: {
      toolbar: {
        show: !1,
      },
    },
    plotOptions: {
      bar: {
        horizontal: !0,
      },
    },
    dataLabels: {
      enabled: !1,
    },
    colors: ChartColors,
    grid: {
      borderColor: "#f1f1f1",
    },
    xaxis: {
      categories,
      labels: {
        formatter: function (value: number) {
          return value.toLocaleString(); // Format numbers with commas only
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        const value = series[seriesIndex][dataPointIndex];
        const formattedValue = value.toLocaleString(); // Format with commas only

        return (
          '<div class="apexcharts-tooltip-box">' +
          '<div class="apexcharts-tooltip-text">' +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">' +
          categories[dataPointIndex] +
          ": </span>" +
          '<span class="apexcharts-tooltip-text-y-value">' +
          formattedValue +
          "</span>" +
          "</div>" +
          "</div>" +
          "</div>"
        );
      },
    },
  };

  return (
    <React.Fragment>
      <ReactApexChart
        dir="ltr"
        className="apex-charts"
        options={options}
        series={series}
        type="bar"
        height={350}
      />
    </React.Fragment>
  );
};

const AmountDistributionChart = ({ series, categories }: any) => {
  const ChartColors = useChartColors("thematic_area_distribution");

  const options: any = {
    chart: {
      toolbar: {
        show: !1,
      },
    },
    plotOptions: {
      bar: {
        horizontal: !0,
      },
    },
    dataLabels: {
      enabled: !1,
    },
    colors: ChartColors,
    grid: {
      borderColor: "#f1f1f1",
    },
    xaxis: {
      categories,
      labels: {
        formatter: function (value: number) {
          if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + "M"; // Convert to millions
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + "K"; // Convert to thousands
          }
          return value.toString(); // Return the original value for smaller numbers
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        const value = series[seriesIndex][dataPointIndex];
        let formattedValue = value.toString();

        if (value >= 1000000) {
          formattedValue = (value / 1000000).toFixed(1) + "M";
        } else if (value >= 1000) {
          formattedValue = (value / 1000).toFixed(1) + "K";
        }

        return (
          '<div class="apexcharts-tooltip-box">' +
          '<div class="apexcharts-tooltip-text">' +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">' +
          categories[dataPointIndex] +
          ": </span>" +
          '<span class="apexcharts-tooltip-text-y-value">' +
          formattedValue +
          "</span>" +
          "</div>" +
          "</div>" +
          "</div>"
        );
      },
    },
  };

  return (
    <React.Fragment>
      <ReactApexChart
        dir="ltr"
        className="apex-charts"
        options={options}
        series={series}
        type="bar"
        height={350}
      />
    </React.Fragment>
  );
};

const ThematicAreaDistribution = () => {
  const [filter] = useQueryState<FilterType>("filter", defaultFilter);

  const [payments, setPayments] = useState([]);
  const [, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsThematicAreaDistribution({
          mdaId: filter?.mda?.value || undefined,
          regionId: filter?.region?.value || undefined,
          paymentCategoryId: filter?.paymentCategory?.value || undefined,
          thematicAreaId: filter?.thematicArea?.value || undefined,
          sectorId: filter?.sector?.value || undefined,
          positionTypeId: filter?.positionType?.value || undefined,
          agencyId: filter?.agency?.value || undefined,
          startDate: filter?.dateRange?.fromDate
            ? moment(filter.dateRange.fromDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
          endDate: filter?.dateRange?.toDate
            ? moment(filter.dateRange.toDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    filter?.agency?.value,
    filter?.dateRange?.fromDate,
    filter?.dateRange?.toDate,
    filter.mda,
    filter?.positionType?.value,
    filter?.region?.value,
    filter?.paymentCategory?.value,
    filter?.sector?.value,
    filter?.thematicArea?.value,
  ]);

  const { countSeries, amountSeries, labels } = React.useMemo(
    () => convertToSeriesAndLabels(payments),
    [payments]
  );

  const [activeTab, setActiveTab] = useState("number");

  return (
    <Card>
      <CardHeader className="d-flex align-items-center">
        <div className="flex-grow-1">
          <h4 className="card-title mb-0">
            {" "}
            Contract Distribution by Thematic Area
          </h4>
        </div>
        <div className="d-flex gap-1">
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "number" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("number")}
          >
            NUMBER
          </button>
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "amount" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("amount")}
          >
            AMOUNT
          </button>
        </div>
      </CardHeader>
      <CardBody>
        {activeTab === "number" ? (
          <ContractDistributionChart series={countSeries} categories={labels} />
        ) : (
          <AmountDistributionChart series={amountSeries} categories={labels} />
        )}
      </CardBody>
    </Card>
  );
};

export default ThematicAreaDistribution;
